import { floatify } from 'lib/maths/mathUtils'

export interface PackageSelectionBurnItem {
  id: string
  price: number
  memberPrice: number
  cashOnlyPrice?: number
  loyaltyProductType: App.LoyaltyProductType
  mx?: string
}

export interface PackageSelectionBurnConversion {
  priceInPoints: number
  isRedeemableWithPoints?: boolean
}

export interface PackageSelectionPayment {
  points: number
  cash: number
}

export interface PackageSelectionBurnQuote {
  regular: PackageSelectionPayment
  member: PackageSelectionPayment
  maximum: number
  regularMaximum: number
  memberMaximum: number
  byId: ReadonlyMap<string, {
    regular: PackageSelectionPayment
    member: PackageSelectionPayment
    regularMaximum: number
    memberMaximum: number
  }>
}

interface ConvertedItem {
  item: PackageSelectionBurnItem
  price: number
  conversion: PackageSelectionBurnConversion
}

function allocate(items: ReadonlyArray<ConvertedItem>, allowance: number) {
  // Checkout spends the shared allowance on the lowest points/currency ratio first.
  const ordered = [...items].sort((left, right) => (
    (left.conversion.priceInPoints / left.price || 0) - (right.conversion.priceInPoints / right.price || 0)
  ))
  const payments = new Map<string, PackageSelectionPayment>()
  let remaining = Math.max(0, allowance)
  let maximum = 0
  for (const { item, price, conversion } of ordered) {
    const redeemable = conversion.isRedeemableWithPoints !== false && price > 0 && conversion.priceInPoints > 0
    const points = redeemable ? Math.min(remaining, conversion.priceInPoints) : 0
    const pointsCashValue = points ? floatify(points * price / conversion.priceInPoints) : 0
    payments.set(item.id, { points, cash: floatify(price - pointsCashValue + (item.cashOnlyPrice ?? 0)) })
    remaining -= points
    maximum += redeemable ? conversion.priceInPoints : 0
  }
  return { payments, maximum }
}

/** Resolve both advertised prices from real conversions; no allowance is reused per room. */
export function getPackageSelectionBurnQuote(
  items: ReadonlyArray<PackageSelectionBurnItem>,
  getConversion: (item: PackageSelectionBurnItem, price: number) => PackageSelectionBurnConversion | undefined,
  allowance: number,
): PackageSelectionBurnQuote | undefined {
  const regularItems: Array<ConvertedItem> = []
  const memberItems: Array<ConvertedItem> = []
  for (const item of items) {
    const regular = item.price > 0 ? getConversion(item, item.price) : { priceInPoints: 0 }
    const member = item.memberPrice > 0 ? getConversion(item, item.memberPrice) : { priceInPoints: 0 }
    if (!regular || !member) return undefined
    regularItems.push({ item, price: item.price, conversion: regular })
    memberItems.push({ item, price: item.memberPrice, conversion: member })
  }
  const regular = allocate(regularItems, allowance)
  const member = allocate(memberItems, allowance)
  const byId = new Map<string, { regular: PackageSelectionPayment, member: PackageSelectionPayment, regularMaximum: number, memberMaximum: number }>()
  for (const [index, item] of items.entries()) {
    const regularConversion = regularItems[index].conversion
    const memberConversion = memberItems[index].conversion
    byId.set(item.id, {
      regular: regular.payments.get(item.id)!,
      member: member.payments.get(item.id)!,
      regularMaximum: regularConversion.isRedeemableWithPoints === false ? 0 : regularConversion.priceInPoints,
      memberMaximum: memberConversion.isRedeemableWithPoints === false ? 0 : memberConversion.priceInPoints,
    })
  }
  const quote = { byId, maximum: regular.maximum, regularMaximum: regular.maximum, memberMaximum: member.maximum }
  return { ...quote, ...getPackageSelectionPaymentTotals(quote, items.map(item => item.id)) }
}

/** Sum a card's assigned rooms without redistributing another room's allowance. */
export function getPackageSelectionPaymentTotals(
  quote: Pick<PackageSelectionBurnQuote, 'byId'>,
  ids: ReadonlyArray<string>,
) {
  let regularPoints = 0
  let regularCash = 0
  let memberPoints = 0
  let memberCash = 0
  for (const id of ids) {
    const payment = quote.byId.get(id)
    if (!payment) continue
    regularPoints += payment.regular.points
    regularCash += payment.regular.cash
    memberPoints += payment.member.points
    memberCash += payment.member.cash
  }
  return {
    regular: { points: regularPoints, cash: floatify(regularCash) },
    member: { points: memberPoints, cash: floatify(memberCash) },
  }
}
